import { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { forceEmailSync, checkEmailMismatch } from '@/utils/emailSync';
import { supabase } from '@/integrations/supabase/client';

/**
 * Component that ensures email is always synced between auth.users and profiles
 * This component runs in the background and handles email synchronization
 * Also detects if user's email exists in Supabase and logs out invalid sessions
 */
export const EmailSyncManager = () => {
  const { user, session, loading, signOut } = useAuth();

  useEffect(() => {
    // Don't run if still loading or no user
    if (loading || !user || !session) return;

    let emailChangeInProgress = false;
    let invalidEmailCheckCount = 0;

    /**
     * Check if the current user's email exists in Supabase auth system
     * If not found, logout the session (handles old email sessions)
     */
    const checkEmailExistsInSupabase = async (): Promise<boolean> => {
      try {
        const currentEmail = user.email;
        if (!currentEmail) return false;

        // Check if this email exists in Supabase auth
        const { data: authUser, error } = await supabase.auth.getUser();
        
        if (error || !authUser?.user) {
          console.warn('EmailSyncManager: No valid auth user found');
          return false;
        }

        // If the auth user email doesn't match session email, it's invalid
        if (authUser.user.email !== currentEmail) {
          console.warn('EmailSyncManager: Session email mismatch with auth user', {
            sessionEmail: currentEmail,
            authEmail: authUser.user.email
          });
          return false;
        }

        return true;
      } catch (error) {
        console.error('EmailSyncManager: Error checking email existence:', error);
        return false;
      }
    };

    const handleEmailValidationAndSync = async () => {
      try {
        // First check if the user's email exists in Supabase
        const emailExists = await checkEmailExistsInSupabase();
        
        if (!emailExists) {
          invalidEmailCheckCount++;
          console.warn(`EmailSyncManager: Invalid email session detected (count: ${invalidEmailCheckCount})`);
          
          // For immediate logout on first check if email change is in progress
          // Otherwise require 2 failed checks to avoid false positives
          const shouldLogout = emailChangeInProgress ? 
            invalidEmailCheckCount >= 1 : 
            invalidEmailCheckCount >= 2;
            
          if (shouldLogout) {
            console.warn('EmailSyncManager: Logging out session with invalid email');
            await signOut();
            return;
          }
        } else {
          // Reset count if email is valid
          invalidEmailCheckCount = 0;
          emailChangeInProgress = false;
          
          // Proceed with normal email sync
          await forceEmailSync();
          console.log('EmailSyncManager: Email sync completed');
        }
      } catch (error) {
        console.error('EmailSyncManager: Error in email validation and sync:', error);
      }
    };

    // Listen for email change events
    const handleAuthStateChange = (event: string) => {
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        emailChangeInProgress = true;
        invalidEmailCheckCount = 0; // Reset count when email change is detected
        console.log('EmailSyncManager: Email change detected, allowing sync time');
        
        // Immediately check for email validity when auth state changes
        setTimeout(() => {
          handleEmailValidationAndSync();
        }, 500); // Very short delay to allow auth state to settle
      }
    };

    // Initial validation after a shorter delay
    const initialTimeout = setTimeout(handleEmailValidationAndSync, 1000);

    // More frequent check every 15 seconds for faster detection
    const syncInterval = setInterval(handleEmailValidationAndSync, 15000);

    // Set up auth state listener for email changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      handleAuthStateChange(event);
    });

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(syncInterval);
      subscription.unsubscribe();
    };
  }, [user, session, loading, signOut]);

  // This component doesn't render anything
  return null;
};